import { FilePlus2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpmpModal } from './PpmpModal';
import type { AnnualProcurementPlanDetail, AppDepartmentRow } from '../procurementPlanTypes';

/**
 * The plan's PPMP surface — a DEPARTMENT ROSTER, and nothing beneath it.
 *
 * **One row per enrolled department, EVERY department**, whether or not it has filed anything.
 * That is what lets the plan say how many offices have started out of how many there are: an
 * officer opening a procurement plan asks who has filed, not for every PPMP across sixty
 * offices, which is a flat list of hundreds of rows with nothing to group them.
 *
 * **A department's own PPMPs are a ROUTE, not a drill-down inside this panel** — the correction
 * the budget plan's own roster took on 21 Sep 2026. A second level with no URL cannot be
 * linked, bookmarked, opened in a tab, or returned to with the browser's own Back.
 *
 * **The ROSTER is the page's**, not this panel's: the plan header counts started-out-of-enrolled
 * from the same rows, so fetching it twice would be two answers to one question.
 */
export function PpmpRosterPanel({
  plan,
  roster,
  onChanged,
}: {
  plan: AnnualProcurementPlanDetail;
  roster: AppDepartmentRow[];
  /** A write moves the plan's own header counts, so the PAGE refetches, not this panel. */
  onChanged: () => void;
}) {
  const navigate = useNavigate();

  /*
    ONE piece of state for both ways in, and it holds the DEPARTMENT or null rather than being
    null when closed. The toolbar's Create opens the form with nobody chosen; a row's entry
    opens it with that department fixed — and `null` cannot mean both "closed" and "no
    department chosen yet".
  */
  const [creating, setCreating] = useState<{ department: AppDepartmentRow | null } | null>(null);

  /*
    Searched, sorted and paged IN MEMORY. The rule against client-side filtering is about a
    PAGED list whose filter silently applies to the rows on screen and not the hundreds behind
    them; the roster arrives WHOLE with the record, so the filter sees everything there is —
    the same exemption a 201-file section takes.
  */
  const fetchRoster = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? roster.filter(
            (row) =>
              row.orgUnitName.toLowerCase().includes(term) ||
              (row.orgUnitCode ?? '').toLowerCase().includes(term) ||
              row.progressLabel.toLowerCase().includes(term),
          )
        : roster;

      const field = query.sort?.replace(/^-/, '') ?? 'orgUnitName';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'ppmpCount'
            ? a.ppmpCount - b.ppmpCount
            : field === 'lineItemCount'
              ? a.lineItemCount - b.lineItemCount
              : field === 'estimatedBudget'
                ? a.estimatedBudget - b.estimatedBudget
                : field === 'progressLabel'
                  ? a.progress - b.progress
                  : a.orgUnitName.localeCompare(b.orgUnitName);

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [roster],
  );

  const state = useDataTable<AppDepartmentRow>({
    fetchPage: fetchRoster,
    defaultSort: 'orgUnitName',
  });

  const rowActions = useCallback(
    (row: AppDepartmentRow): MenuItem[] => [
      {
        key: `file-${row.ppmpDepartmentId}`,
        label: 'File a PPMP',
        icon: <FilePlus2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'ppmps.create',
        /*
          DISABLED for state with the reason, because the server refuses it too — and the flag
          it reads is the same one the endpoint checks, so the sentence and the refusal cannot
          disagree.
        */
        disabledReason: plan.isSubmissionOpen
          ? null
          : 'The submission window for this plan has closed.',
        onClick: () => setCreating({ department: row }),
      },
    ],
    [plan.isSubmissionOpen],
  );

  return (
    <>
      <DataTablePanel<AppDepartmentRow>
        id="app-roster"
        /*
          FILL, as the budget plan's own PPA roster is — the twin one module along. Without it
          the panel is sized by its rows, so the totals footer floats up under the last one and
          the pager sits in a field of dead space instead of on the panel's bottom edge.
        */
        fill
        state={state}
        columns={COLUMNS}
        rowKey={(row) => row.ppmpDepartmentId}
        /*
          NOTHING acts on a selection here — a roster row is a VIEW of one department's filing,
          not a record anything can be done to in bulk. That is the narrow case the opt-out is
          for, rather than "bulk actions are merely unbuilt".
        */
        selectable={false}
        /*
          A roster row is DERIVED — computed at read time from the enrolment and the PPMPs
          beneath it — so it has no creator, no creation instant and no update trail. `null`
          here owes `auditColumns={false}` in the same breath, or three columns of em dashes
          arrive instead.
        */
        otherInfo={() => null}
        auditColumns={false}
        onRowOpen={(row) =>
          navigate(
            `/procurement/procurement-planning/annual-procurement-plans/${plan.id}/departments/${row.orgUnitId}`,
          )
        }
        openPermission="ppmps.read"
        rowActions={rowActions}
        onCreate={() => setCreating({ department: null })}
        createPermission="ppmps.create"
        createDisabledReason={
          plan.isSubmissionOpen
            ? roster.length === 0
              ? 'Nobody is enrolled to file a PPMP under this plan.'
              : null
            : 'The submission window for this plan has closed.'
        }
        searchPlaceholder="Search departments"
        emptyMessage="Nobody is enrolled to file a PPMP. Enrol the offices that procure under Procurement Planning → Settings — this roster is built from that list."
        emptySearchMessage="No department matches that search."
      />

      {creating !== null ? (
        <PpmpModal
          open
          plan={plan}
          /* Fixed where a row opened it, and chosen on the form where the toolbar did. */
          department={creating.department}
          departments={roster}
          editing={null}
          onClose={() => setCreating(null)}
          onSaved={() => {
            setCreating(null);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: DataColumn<AppDepartmentRow>[] = [
  {
    key: 'orgUnitName',
    header: 'Department',
    width: 280,
    sortable: true,
    render: (row) => <TextCell value={row.orgUnitName} />,
  },
  {
    key: 'orgUnitCode',
    header: 'Unit code',
    width: 140,
    // A PATH through the chart. Mono because it is an identifier, never `numeric`.
    render: (row) => <TextCell value={row.orgUnitCode} mono />,
  },
  {
    key: 'progressLabel',
    header: 'Status',
    width: 150,
    sortable: true,
    stage: true,
    /*
      PLAIN TEXT through a `stage` column, not the `status: true` pill. A roster row has no
      live-or-deleted state of its own — every row is a live enrolment of a live unit — so the
      one state it DOES carry is its progress, and the shell places it beside where a status
      pill would be.

      The LABEL is the server's. The wire carries the value and its words together, so nothing
      here re-derives them and the two cannot drift.
    */
    render: (row) => <StatusText tone="inactive">{row.progressLabel}</StatusText>,
  },
  {
    key: 'ppmpCount',
    header: 'PPMPs',
    width: 100,
    align: 'right',
    sortable: true,
    render: (row) => <TextCell value={String(row.ppmpCount)} mono numeric />,
  },
  {
    key: 'lineItemCount',
    header: 'Line items',
    width: 110,
    align: 'right',
    sortable: true,
    /*
      NOT the same fact as the count beside it — one PPMP carrying twenty lines and twenty
      carrying one read identically without it. The budget roster draws the same distinction.
    */
    render: (row) => <TextCell value={String(row.lineItemCount)} mono numeric />,
  },
  {
    key: 'estimatedBudget',
    header: 'Estimated budget',
    width: 180,
    align: 'right',
    sortable: true,
    footer: (rows) => formatMoney(rows.reduce((sum, row) => sum + row.estimatedBudget, 0)),
    render: (row) => <TextCell value={formatMoney(row.estimatedBudget)} mono numeric />,
  },
];
